<template>
  <div
    v-if="!checkPermission('manage_settings') || forbidden"
    class="settings-css manage" >
    <h1 class="settings__title" >
      <div
        class="back-btn"
        @click="goBack('WAManage')">
        <SBIcon
          class="mr-2"
          :icon="SBIconList.DOWN_ARROW"
          :color="SBColors.greyPrimary"
          :size="12"
          :rotate="90" />
        <p>Webhook logs</p>
      </div>
    </h1>
    <Forbidden />
  </div>
  <div
    v-else
    ref="contentRef"
    class="flex column settings-css manage parent-flex">
    <div class="settings--limit-width flex column">
      <h1 class="settings__title" >
        <div
          class="back-btn"
          @click="goBack('WAManage')">
          <SBIcon
            class="mr-2"
            :icon="SBIconList.DOWN_ARROW"
            :color="SBColors.greyPrimary"
            :size="12"
            :rotate="90" />
          <p>Webhook logs</p>
        </div>
      </h1>

      <span class="flex date-picker-wrapper">
        <!-- <DatePicker
          ref="dateRange"
          :start-date="business.created_at"
          :omit-shortcuts="['yesterday', 'last365Days']"
          @datesChanged="filterDates"/> -->
        <b-button
          class="flex center-all"
          @click="refresh">
          <SBIcon
            class="mr-2"
            :icon="SBIconList.REFRESH_DOUBLE"
            :color="SBColors.blackPrimary"
            :spin="refreshing"
            :size="12"/>
          <span>Refresh</span>
        </b-button>

        <span class="greedy" />
      </span>

      <div
        v-if="!webhookLogsLoaded && page === 1"
        class="no-data flex center-all">
        <Spinner
          :size="20"
          :thickness="2" />
      </div>

      <template v-else-if="logsAppended.length > 0">
        <b-row class="data-wrapper greedy">
          <b-col
            class="m-0 p-0 logs__list"
            lg="6" >
            <div
              v-for="(log, index) in logsAppended"
              :key="index" >
              <!-- Date mark -->
              <p
                v-if="index === 0 || index > 0 && !isSameDay(log.created_at, logsAppended[index - 1].created_at)"
                class="log__time-flag"
                :class="{'include-top-margin': index !== 0}" >
                {{ log.created_at | messagesMainDate }}
              </p>
              <!-- Log item -->
              <div
                class="log__item flex center-all"
                :class="{active: log.id === selectedLog.id}"
                @click="selectLog(log)" >
                <span
                  class="log__status"
                  :class="getStatusColor(log.response_status_code)">{{ log.response_status_code }}</span>
                <span class="log__type">POST</span> <!-- TODO POST HARDCODED for now since we do not have it in the response. -->
                <span class="log__url greedy">{{ log.endpoint_url }}</span>
                <span class="log__date">{{ log.created_at | timeFull }}</span>
              </div>
            </div>

            <Paging
              :current-page="webhookLogs.meta.current_page"
              :total-pages="webhookLogs.meta.last_page"
              :load-more-mode="true"
              :loading="!webhookLogsLoaded"
              class="bottom-40"
              @page="loadPage" />
          </b-col>
          <b-col
            class="log-data"
            lg="6" >
            <ScrollArea
              v-if="selectedLog && selectedLog.response_status_code"
              ref="webhookLogsScrollbar" >
              <div class="bottom-40">
                <h3><strong>POST</strong> {{ selectedLog.endpoint_url }}</h3> <!-- POST HARDCODED for now since we do not have it in the response. -->

                <div class="log-info">
                  <span class="log-info-title">Status</span>
                  <span
                    class="log__status"
                    :class="getStatusColor(selectedLog.response_status_code)">{{ selectedLog.response_status_code }}</span>
                </div>
                <div class="log-info">
                  <span class="log-info-title">Time</span>
                  <span class="log-info-value">{{ selectedLog.created_at | dateTimeFull }}</span>
                </div>

                <div class="divider" />

                <!-- Request headers -->
                <div class="log-data-entry">
                  <h5>Request headers</h5>
                  <div class="json-pretty" v-html="syntaxHighlight(requestHeaders)" />
                </div>
                <!-- Request body -->
                <div class="log-data-entry">
                  <h5>Request body</h5>
                  <div class="json-pretty" v-html="syntaxHighlight(requestBody)" />
                </div>
                <!-- Response headers -->
                <div class="log-data-entry">
                  <h5>Response headers</h5>
                  <div class="json-pretty" v-html="syntaxHighlight(responseHeaders)" />
                </div>
                <!-- Response body -->
                <div class="log-data-entry">
                  <h5>Response body</h5>
                  <div class="json-pretty" v-html="syntaxHighlight(responseBody)" />
                </div>
              </div>
            </ScrollArea>
          </b-col>
        </b-row>
      </template>
      <div
        v-else
        class="no-data flex center-all" >
        <span class="wrapper flex center-all">
          <span class="emoji">😐</span>
          <p>No data is available at the moment</p>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import moment from '@/helpers/momentz';

import Spinner from '@/components/common/Spinner';
import Paging from '@/components/common/Paging';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import ScrollArea from '@/components/common/ScrollArea';
import NoPermissionMixin from '@/mixins/AccessPermission';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';
import CommonActions from '@/components/manage/CommonActions.mixin';

export default {
  name: 'WebhookLogsSettings',
  components: {
    Spinner,
    Paging,
    SBIcon,
    ScrollArea,
  },
  mixins: [
    NoPermissionMixin,
    UserPermissionsMixin,
    CommonActions,
  ],
  data() {
    return {
      refreshing: false,
      selectedLog: null,
      logsAppended: [],
      dateRange: {
        startDate: '',
        endDate: '',
      },
      page: 1,
      limit: 20,
      SBIconList,
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      webhookLogs: 'webhook/webhookLogs',
      webhookLogsLoaded: 'webhook/webhookLogsLoaded',
      userPermissions: 'user/userPermissions',
      currentOrganisation: 'organisation/currentOrganisation',
    }),
    business() {
      return this.currentOrganisation.businesses.find(b => b.id === this.$route.params.businessId);
    },
    requestHeaders() {
      let requestHeaders = {};
      if (this.selectedLog && this.selectedLog.request_headers && this.selectedLog.request_headers !== '\'\'') {
        requestHeaders = this.safeJsonSingleQouteParse(this.selectedLog.request_headers);
      }
      return JSON.stringify(requestHeaders, undefined, 4);
    },
    requestBody() {
      let requestBody = {};
      if (this.selectedLog && this.selectedLog.request_body && this.selectedLog.request_body !== '\'\'') {
        requestBody = this.safeJsonSingleQouteParse(this.selectedLog.request_body);
      }
      return JSON.stringify(requestBody, undefined, 4);
    },
    responseHeaders() {
      let responseHeaders = {};
      if (this.selectedLog && this.selectedLog.response_headers && this.selectedLog.response_headers !== '\'\'') {
        responseHeaders = this.safeJsonSingleQouteParse(this.selectedLog.response_headers);
      }
      return JSON.stringify(responseHeaders, undefined, 4);
    },
    responseBody() {
      let responseBody = {};
      if (this.selectedLog && this.selectedLog.response_body && this.selectedLog.response_body !== '\'\'') {
        responseBody = this.safeJsonSingleQouteParse(this.selectedLog.response_body);
      }
      return JSON.stringify(responseBody, undefined, 4);
    },
  },
  watch: {
    business() {
      if (this.checkPermission('manage_settings') && this.business.id) {
        this.page = 1;
        this.logsAppended = [];
        this.selectedLog = {};
        this.loadWebhookLogs();
      }
    },
    webhookLogsLoaded() {
      if (this.webhookLogsLoaded) {
        this.refreshing = false;
        if (this.page === 1) {
          this.selectLog(this.webhookLogs.data[0]);
        }
        this.logsAppended = [...this.logsAppended, ...this.webhookLogs.data];
      }
    },
    userPermissions() {
      if (this.checkPermission('manage_settings') && this.business.id) {
        this.page = 1;
        this.logsAppended = [];
        this.selectedLog = {};
        this.loadWebhookLogs();
      }
    },
  },
  mounted() {
    // in case user returns to the page. On first mount business is undefined so this will not trigger
    if (this.checkPermission('manage_settings') && this.business.id) {
      this.loadWebhookLogs();
    }

    // this.safeJsonSingleQouteParse("{'business': {'id': '27e6a\"caa-8f5a-4736-9032-682e02\"06d3e7', 'name': 'Zippi'},\n 'channel': 'whatsapp',\n 'content': {'text': 'Esse é o seu código Zippi: 123456'},\n 'content_type': 'text',\n 'conversation_id': '6ad2e7f5-320d-43ae-ab6e-4292ab7f3b65',\n 'destination': '+5511972207712',\n 'direction': 'outbound',\n 'error': 'None',\n 'sent_at': '2020-03-05T20:19:58.505859Z',\n 'source': '+5511934456246',\n 'status': 'read',\n 'type': 'message.sent_status'}");
    this.finalizeSplashView();
  },
  methods: {
    ...mapActions({
      fetchWebhookLogs: 'webhook/fetchWebhookLogs',
      finalizeSplashView: 'organisation/finalizeSplashView',
    }),
    checkNoneValues(stringifiedJson) {
      stringifiedJson = stringifiedJson.replace(/:\s*None/gi, '"None"');
      stringifiedJson = stringifiedJson.replace(/\[empty\]/gi, '{}');
      return stringifiedJson;
    },
    /**
     * Sometimes we have a situation like this
     * "error": "This whatsapp 'destination' is not in free form window and no "
     *          "template matched with your content",
     * One string is broken into two lines and both are wrapped with quotes.
     * So we will fix it by replacing '"\n*\s*"' with ''
     */
    checkDuplicatedQuotes(stringifiedJson) {
      return stringifiedJson.replace(/"\n*\s*"/g, '');
    },
    /**
     * Sometimes we end up with a situation like this and we need to fix it.
     * "error": 'This whatsapp "destination" is not in free form window and no '
     *          "template matched with your content",
     * We have a mix of single and doble quotes and we need to invert the ones starting with singles.
     */
    checkInvertedQuotes(stringifiedJson) {
      stringifiedJson = stringifiedJson.replace(/'(\w\s*)*"(\w\s*)*?"(.*)*'/g, (match) => {
        const safeReplaceDoubleQuotes = match.replace(/"/g, ':::DQ:::');
        const safeReplaceSingleQuotes = safeReplaceDoubleQuotes.replace(/'/g, '"');
        const convertedDQtoSQ = safeReplaceSingleQuotes.replace(/:::DQ:::/g, '\'');
        return convertedDQtoSQ;
      });

      return stringifiedJson;
    },
    processNewLines(stringifiedJson) {
      return stringifiedJson.replace(/'\s*'/g, '');
    },
    /** Parses json stringified data to object even if json is wrapped with singlequotes instead of double quotes. */
    safeJsonSingleQouteParse(stringifiedJSON) {
      let json;
      // Test quote type
      const firstQuote = stringifiedJSON.replace('{', '').trim().substr(0, 1);

      try {
        console.log('fQ', firstQuote);
        if (firstQuote === '\'') {
          const newLinesRemoved = this.processNewLines(stringifiedJSON);
          const safeReplaceDoubleQuotes = newLinesRemoved.replace(/"/g, ':::DQ:::');
          const safeReplaceSingleQuotes = safeReplaceDoubleQuotes.replace(/'/g, '"');
          const convertedDQtoSQ = safeReplaceSingleQuotes.replace(/:::DQ:::/g, '\'');
          const invertedQuotesChecked = this.checkInvertedQuotes(convertedDQtoSQ);
          const duplicatedQuotesRemoved = this.checkDuplicatedQuotes(invertedQuotesChecked);
          const finalJsonVersion = this.checkNoneValues(duplicatedQuotesRemoved);
          console.log('fJ', finalJsonVersion);
          json = JSON.parse(finalJsonVersion);
        } else {
          const finalJsonVersion = this.checkNoneValues(stringifiedJSON);
          console.log('fJ', finalJsonVersion);
          json = JSON.parse(finalJsonVersion);
        }
      } catch (error) {
        return JSON.parse('{ "error": "Error parsing JSON" }');
      }

      return json;
    },
    getStatusColor(statusCode) {
      const statusFirstNumber = statusCode.toString().charAt(0);

      switch (statusFirstNumber) {
        case '2': {
          return 'green';
        }
        case '3':
        case '4': {
          return 'yellow';
        }
        case '5': {
          return 'red';
        }
        default: {
          return 'yellow';
        }
      }
    },
    filterDates(dates) {
      if (dates.length) {
        this.dateRange = {
          startDate: dates[0].unix(),
          endDate: dates[1].unix(),
        };
      } else {
        this.dateRange = {
          startDate: '',
          endDate: '',
        };
      }

      this.page = 1;
      this.logsAppended = [];
      this.selectedLog = {};
      this.loadWebhookLogs();
    },
    isSameDay(testDate, compareDate) {
      return moment(testDate).isSame(moment(compareDate), 'day');
    },
    loadPage(pageNumber) {
      this.page = pageNumber;
      this.loadWebhookLogs();
    },
    refresh() {
      this.refreshing = true;
      this.page = 1;
      this.logsAppended = [];
      this.selectedLog = {};
      this.loadWebhookLogs();
    },
    loadWebhookLogs() {
      if (this.business.id) {
        this.fetchWebhookLogs({
          merchant_id: this.business.id,
          data: {
            limit: this.limit,
            page: this.page,
            start_date: this.dateRange.startDate,
            end_date: this.dateRange.endDate,
          },
        });
      }
    },
    selectLog(log) {
      this.selectedLog = log;
    },
    syntaxHighlight(json) {
      let counter = 1;
      json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      json = json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?|\n)/g, (match) => {
        let cls = 'number';
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'key';
          } else {
            cls = 'string';
          }
        } else if (/true|false/.test(match)) {
          cls = 'boolean';
        } else if (/null/.test(match)) {
          cls = 'null';
        } else if (/\n/.test(match)) {
          counter++;
          return `${match}<span class="row-number">${counter}</span>`;
        }
        return `<span class="${cls}">${match}</span>`;
      });
      json = json.replace(/^{}$/, '<span class="row-number ml-0">empty</span>');
      json = `<span class="row-number">1</span>${json}`;
      return json;
    },
  },
};
</script>

<style lang="scss" scoped>
.parent-flex {
  width: -webkit-fill-available;
}

.settings--limit-width {
  max-height: 100% !important;
}

::v-deep .bottom-40 {
  margin-bottom: 40px;
}

::v-deep .json-pretty {
  white-space: pre;
  font-size: 14px;
  margin-left: 0px;
  overflow-x: auto;
  padding-bottom: 10px;

  * {
    font-size: 14px;
    font-family: monospace;
    line-height: 1.3;
  }

  .string { color: $bluePrimary; }
  .number { color: $redPrimary; }
  .boolean { color: $greenPrimary; }
  .null { color: #CEA111; }
  .key { color: $blackPrimary; }

  span {
    word-break: break-all;
  }

  .row-number {
    display: inline-block;
    min-width: 25px;
    color: $greyPrimary;
  }
}

.data-wrapper {
  overflow: hidden;
  border-top: 1px solid $greyBorderSecondary;
}

.date-picker-wrapper {
  margin-top: 35px;
  margin-bottom: 15px;
}

.row {
  margin: 0 !important;
}

.log-data {
  height: 100%;
  overflow: auto;
  padding: 0 0 0 40px;

  .v-scroll-visible {
    padding-right: 40px;
  }

  h3 {
    margin: 20px 0 20px 0;
    font-size: 16px;
    font-weight: 400;
    color: $blackPrimary;
    cursor: default;

    strong {
      font-weight: 500;
    }
  }

  .log-info {
    cursor: default;

    .log-info-title {
      font-size: 14px;
      font-weight: 400;
      color: $blackUltralight;
      margin-right: 8px;
    }

    .log-info-value {
      font-size: 14px;
      font-weight: 400;
      color: $blackPrimary;
    }

    .log__status {
      font-size: 12px;
      font-weight: 500;
      border-radius: 3px;
      margin-bottom: 8px;
      display: inline-block;

      &.green {
        padding: 3px 8px;
        color: #00bfa5;
        background-color: #cdf3ef;
      }

      &.yellow {
        padding: 3px 8px;
        color: #cea111;
        background-color: #fff7dd;
      }

      &.red {
        padding: 3px 8px;
        color: #ff5b61;
        background-color: #ffebec;
      }
    }
  }

  .divider {
    margin-bottom: 20px;
  }

  .log-data-entry {
    padding: 20px 0 0 0;
    margin: 10px 0 0 0;
    border-top: 1px solid $greyBorderSecondary;

    h5 {
      font-size: 14px;
      font-weight: 500;
      color: $blackPrimary;
      margin: 0 0 15px 0;
    }
  }
}

::v-deep .logs__list {
  height: 100%;
  overflow: auto;
  padding-top: 20px !important;
  padding-right: 40px !important;
  border-right: 1px solid $greyBorderSecondary;

  .log__time-flag {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 12px;

    &.include-top-margin {
      margin-top: 17px;
    }
  }

  .log__item {
    padding: 8px 10px;
    margin-bottom: 2px;
    border-radius: 5px;
    cursor: pointer;

    .log__status {
      font-size: 12px;
      font-weight: 500;
      border-radius: 3px;

      &.green {
        padding: 3px 8px;
        color: #00bfa5;
        background-color: #cdf3ef;
      }

      &.yellow {
        padding: 3px 8px;
        color: #cea111;
        background-color: #fff7dd;
      }

      &.red {
        padding: 3px 8px;
        color: #ff5b61;
        background-color: #ffebec;
      }
    }

    .log__type {
      font-family: monospace;
      font-size: 14px;
      font-weight: 500;
      padding-left: 12px;
      padding-right: 10px;
      color: $blackUltralight;
    }

    .log__url,
    .log__date {
      font-size: 14px;
      font-weight: 400;
      color: $blackUltralight;
      white-space: nowrap;
    }

    .log__url {
      font-family: monospace;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-right: 15px;
    }

    &:hover {
      background-color: #fafbfc;
      .log__url, .log__date, .log__type {
        color: $blackPrimary;
      }
    }

    &.active {
      background-color: #f6f8f9;
      .log__url, .log__date, .log__type {
        color: $blackPrimary;
      }
    }
  }
}
</style>
